import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSite } from '../context/SiteContext';
import '../assets/LegalPage.css';

const ReturnsPolicy = () => {
  const { siteName, fullAddress, email, phone } = useSite();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const effectiveDate = 'March 19, 2025';
  const lastUpdated = 'March 19, 2025';

  return (
    <div className="legal-page">
      <div className="legal-container">
        <header className="legal-header">
          <h1>Returns & Refunds Policy</h1>
          <p className="legal-meta">
            Effective Date: {effectiveDate} | Last Updated: {lastUpdated}
          </p>
        </header>

        <div className="legal-content">
          <section className="legal-section">
            <h2>1. Our Return Policy</h2>
            <p>
              At {siteName}, we want you to be completely satisfied with your purchase. Most items can be returned within 30 days of delivery for a full refund or exchange, subject to the conditions below. This policy applies to purchases made through our website.
            </p>
          </section>

          <section className="legal-section">
            <h2>2. Return Eligibility</h2>
            <p>To be eligible for a return, items must:</p>
            <ul>
              <li>Be returned within 30 days of the delivery date</li>
              <li>Be unused, unworn, and in original condition with all tags attached</li>
              <li>Be in original packaging where applicable</li>
              <li>Not be final sale items (see Section 5)</li>
            </ul>
            <p>
              We reserve the right to refuse returns that do not meet these conditions. Items that appear worn, altered, or damaged may be rejected or subject to a reduced refund.
            </p>
          </section>

          <section className="legal-section">
            <h2>3. How to Return an Item</h2>
            <ol>
              <li><strong>Initiate a return:</strong> Log into your account, go to Order History, and select "Return Item" for the order. Alternatively, contact Customer Service at <a href={`mailto:${email}`}>{email}</a> or <a href={`tel:${phone}`}>{phone}</a>.</li>
              <li><strong>Receive your return label:</strong> We will email you a prepaid return shipping label. For returns initiated through your account, the label may be available for immediate download.</li>
              <li><strong>Pack your item:</strong> Securely pack the item in its original packaging (or suitable packaging) and include the packing slip or order confirmation.</li>
              <li><strong>Ship your return:</strong> Drop off the package at any authorized carrier location. Keep your tracking number for reference.</li>
            </ol>
            <p>
              Returns must be postmarked within 30 days of delivery. We recommend using a trackable shipping service. We are not responsible for returns lost in transit.
            </p>
          </section>

          <section className="legal-section">
            <h2>4. Refunds</h2>
            <p>
              Once we receive and inspect your return, we will notify you of the approval or rejection of your refund. If approved, your refund will be processed within 5–10 business days. Refunds will be credited to the original payment method. Depending on your financial institution, it may take an additional 3–5 business days for the refund to appear on your statement.
            </p>
            <p>
              <strong>Original shipping charges</strong> are non-refundable unless the return is due to our error (e.g., wrong item shipped, defective product). <strong>Return shipping</strong> is free for eligible returns when using our prepaid label. If you choose to use your own carrier, you are responsible for return shipping costs.
            </p>
          </section>

          <section className="legal-section">
            <h2>5. Non-Returnable Items</h2>
            <p>The following items cannot be returned:</p>
            <ul>
              <li>Final sale items (clearly marked at time of purchase)</li>
              <li>Personal care and beauty products (for health and safety reasons)</li>
              <li>Swimwear and intimate apparel (unless tags are attached and items are unworn)</li>
              <li>Gift cards</li>
              <li>Custom or personalized items</li>
              <li>Items that have been worn, altered, or damaged by the customer</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>6. Exchanges</h2>
            <p>
              For a different size, color, or style, we recommend returning the original item for a refund and placing a new order. This ensures the fastest processing. If an item is out of stock, we will process a refund. Exchanges may be available for select items—please contact Customer Service to inquire.
            </p>
          </section>

          <section className="legal-section">
            <h2>7. Defective or Damaged Items</h2>
            <p>
              If you receive a defective or damaged item, please contact us within 7 days of delivery. We may request photos as proof. We will arrange for a replacement or full refund, including shipping costs. Do not return defective items until you have received instructions from our team.
            </p>
          </section>

          <section className="legal-section">
            <h2>8. Late or Missing Refunds</h2>
            <p>
              If you have not received your refund after 10 business days, first check your bank account again and contact your credit card company. If you have done this and still have not received your refund, please contact us at <a href={`mailto:${email}`}>{email}</a>.
            </p>
          </section>

          <section className="legal-section">
            <h2>9. State-Specific Rights</h2>
            <p>
              Some states grant consumers additional rights regarding returns and refunds. Nothing in this policy limits any rights you may have under applicable state law.
            </p>
          </section>

          <section className="legal-section">
            <h2>10. Contact Us</h2>
            <p>
              For questions about returns or refunds, please contact us:
            </p>
            <ul className="legal-contact">
              <li><strong>{siteName}</strong></li>
              <li>{fullAddress}</li>
              <li>Email: <a href={`mailto:${email}`}>{email}</a></li>
              <li>Phone: <a href={`tel:${phone}`}>{phone}</a></li>
            </ul>
          </section>
        </div>

        <div className="legal-footer-links">
          <Link to="/">Return to Home</Link>
          <Link to="/terms">Terms of Use</Link>
          <Link to="/shipping">Shipping Policy</Link>
          <Link to="/contact">Contact Us</Link>
        </div>
      </div>
    </div>
  );
};

export default ReturnsPolicy;
